import React from 'react';

import { TIME_FILTER_TYPE_OPTIONS } from '@vlabs/api-bindings/src/constants';
import { SENDING_TYPE_OPTIONS } from '@vlabs/api-bindings/src/luna-client/constants';
import { InfoCircleIcon } from '@vlabs/icons';
import validate from '@vlabs/shared/validators';
import { Control, SettingsItemWrapper, Tooltip } from '@vlabs/uikit';
import { Controller, useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { defaultValues } from '@vlabs/pages/video-analytics/defaultFormValues';

import { DroiForm } from './DroiForm';
import { EventPolicySubForm } from './EventPolicySubForm';
import { SaveToArchiveSubForm } from './SaveToArchiveSubForm';
import st from './SharedSubFormStyles.module.sass';
import { TargetsOverviewSubForm } from './TargetsOverviewSubForm';
import {
  EventThresholdSettingsModal,
  EventThresholdSettingsTooltipPreview,
} from '../../modal/EventThresholdSettingsModal';
import { FrameAnalysisFrequencyModal } from '../../modal/FrameAnalysisFrequencyModal';

const commonStylesProps = {
  className: st.SettingItem,
  titleClassName: st.SettingItem__Title,
  contentClassName: st.SettingItem__Content,
};

export function WeaponSubForm({ fieldName }) {
  const { t } = useTranslation();

  const createFullName = (name) => (fieldName ? `${fieldName}.${name}` : name);

  const { register, control, formState: { errors } } = useFormContext();
  const frameRateAnalysisType = useWatch({ name: createFullName('parameters.parameters.rate.unit') })?.value;
  const targetsField = useWatch({ name: createFullName('parameters.targets'), defaultValue: [] });
  const isTargetsWeaponActive = targetsField.includes('weapon');

  return (
    <React.Fragment>
      <div className={st.SettingsGroup}>
        <div className={st.SettingsGroup__Header}>
          <span>{t('videoAnalytics:form.threshold_settings.title')}</span>
          <Tooltip
            className={st.Tooltip}
            modalContent={<EventThresholdSettingsModal />}
            tooltipContent={<EventThresholdSettingsTooltipPreview />}
            tooltipProps={{ effect: 'solid' }}
          >
            <InfoCircleIcon className={st.Tooltip_icon} />
          </Tooltip>
        </div>

        <SettingsItemWrapper
          data-tooltip-content={t('videoAnalytics:form.weapon.weapon_threshold.tooltip')}
          title={t('videoAnalytics:form.weapon.weapon_threshold.label')}
          {...commonStylesProps}
        >
          <Control.Input
            {...register(createFullName('parameters.parameters.weapon_threshold'), {
              min: validate.gte(0),
              max: validate.lte(1),
              validate: validate.number(),
            })}
            errors={errors}
          />
        </SettingsItemWrapper>

        <SettingsItemWrapper
          data-tooltip-content={t('videoAnalytics:form.weapon.time_filter.type.tooltip')}
          title={t('videoAnalytics:form.weapon.time_filter.type.label')}
          {...commonStylesProps}
        >
          <Control.Select
            control={control}
            isClearable
            name={createFullName('parameters.parameters.time_filter.type')}
            options={TIME_FILTER_TYPE_OPTIONS.raw}
          />
        </SettingsItemWrapper>

        <SettingsItemWrapper
          data-tooltip-content={t('videoAnalytics:form.weapon.time_filter.length.tooltip')}
          title={t('videoAnalytics:form.weapon.time_filter.length.label')}
          {...commonStylesProps}
        >
          <Control.Input
            {...register(createFullName('parameters.parameters.time_filter.length'), {
              min: validate.gte(1),
              validate: validate.integer(),
            })}
            errors={errors}
          />
        </SettingsItemWrapper>
      </div>

      <div className={st.SettingsGroup}>
        <SettingsItemWrapper
          data-tooltip-content={t('videoAnalytics:form.body_detection_size.tooltip')}
          title={t('videoAnalytics:form.body_detection_size.label')}
          {...commonStylesProps}
        >
          <Control.Input
            {...register(createFullName('parameters.parameters.min_body_detection_size'), {
              min: validate.gte(0),
              validate: validate.integer(),
            })}
            errors={errors}
          />
        </SettingsItemWrapper>

        <SettingsItemWrapper
          title={t('videoAnalytics:form.rate.unit.label')}
          {...commonStylesProps}
        >
          <Control.Select
            control={control}
            isClearable
            name={createFullName('parameters.parameters.rate.unit')}
            options={SENDING_TYPE_OPTIONS.raw}
          />
        </SettingsItemWrapper>

        <SettingsItemWrapper
          data-tooltip-content={t('videoAnalytics:form.rate.period.tooltip')}
          modalContent={<FrameAnalysisFrequencyModal />}
          title={t('videoAnalytics:form.rate.period.label')}
          {...commonStylesProps}
        >
          <Control.Input
            {...register(createFullName('parameters.parameters.rate.period'), {
              min: frameRateAnalysisType === 'frame'
                ? validate.gte(1)
                : validate.gte(0.001),
              validate: frameRateAnalysisType === 'frame'
                ? validate.integer()
                : validate.number(),
            })}
            errors={errors}
          />
        </SettingsItemWrapper>

        <EventPolicySubForm fieldName={fieldName} />

        <SettingsItemWrapper
          data-tooltip-content={t('videoAnalytics:form.timeout_interval.tooltip')}
          title={t('videoAnalytics:form.timeout_interval.label')}
          {...commonStylesProps}
        >
          <Control.Input
            {...register(createFullName('parameters.parameters.timeout_interval'), {
              min: validate.gte(0),
              validate: validate.integer(),
            })}
            errors={errors}
          />
        </SettingsItemWrapper>

        <SettingsItemWrapper
          title={t('videoAnalytics:form.ROI.label')}
          {...commonStylesProps}
        >
          <Controller
            control={control}
            name={createFullName('parameters.parameters.roi')}
            render={({ field: { value, onChange } }) => {
              return (
                <Control.CoordinatesInput
                  errors={errors}
                  name={createFullName('parameters.parameters.roi')}
                  onChange={onChange}
                  value={value ?? defaultValues.roi}
                  variant="modern"
                />
              );
            }}
            rules={{ validate: validate.region }}
          />
        </SettingsItemWrapper>

        <DroiForm name={createFullName('parameters.parameters.droi')} />

        <SettingsItemWrapper
          title={t('videoAnalytics:form.weapon.targets.weapon')}
          {...commonStylesProps}
        >
          <Control.Checkbox
            id={createFullName('parameters.targets.weapon')}
            label={t(`videoAnalytics:form.${isTargetsWeaponActive ? 'enabled' : 'disabled'}`)}
            {...register(createFullName('parameters.targets.weapon'))}
          />
        </SettingsItemWrapper>

        <TargetsOverviewSubForm
          analytic="weapon"
          fieldName={fieldName}
        />

        <SaveToArchiveSubForm fieldName={fieldName} />
      </div>
    </React.Fragment>
  );
}
